import * as React from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Menu } from "lucide-react";
import Sidebar from "./Sidebar";
import CommandPalette from "./CommandPalette";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { CommsUnreadProvider } from "@/context/CommsUnreadContext";
import { useCompanyEvents } from "@/hooks/useCompanyEvents";
import ActiveTimerPill from "@/components/boards/ActiveTimerPill";
import ConnectionPill from "@/components/feel/ConnectionPill";
import ShortcutsHelp from "@/components/feel/ShortcutsHelp";
import { toast } from "sonner";

/**
 * AppShell: sidebar + main pane + palette + SSE wiring.
 * Emits toasts on entitlements/role/member events so any open page reacts.
 * Broadcasts events on a window CustomEvent so pages can subscribe.
 *
 * Feel-spec additions (Phase 8):
 * - ConnectionPill (SSE live/reconnecting badge)
 * - ShortcutsHelp (global `?` overlay)
 * - Route transitions via `animate-page-in` on the Outlet key
 */
export default function AppShell() {
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const { companyId } = useParams();
  const { company, refresh: refreshCompany } = useCompany();

  const onEvent = React.useCallback((evt) => {
    // Broadcast for pages
    window.dispatchEvent(new CustomEvent("fable:event", { detail: evt }));

    switch (evt.type) {
      case "entitlements.updated":
      case "role.updated":
      case "role.deleted":
      case "member.role_changed":
        refreshCompany();
        break;
      case "member.removed":
        if (evt.payload?.user_id === user?.id) {
          toast.error("You were removed from this company");
          nav("/app");
        }
        break;
      default:
        break;
    }
  }, [refreshCompany, user, nav]);

  const { connected } = useCompanyEvents(companyId, onEvent);

  // Scroll-restoration: on back navigation the browser tries; on forward
  // navigation we scroll the main pane to top so route transitions feel
  // clean.
  const mainRef = React.useRef(null);
  React.useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <CommsUnreadProvider>
      <div className="min-h-screen flex" data-testid="app-shell">
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onOpenPalette={() => setPaletteOpen(true)}
        />
        <div className="flex-1 min-w-0 flex flex-col">
          <div
            className="lg:hidden sticky top-0 z-20 bg-card border-b border-brand-rule flex items-center gap-3 px-4 h-14"
            data-testid="app-topbar"
          >
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="btn-ghost h-9 w-9 !p-0"
              aria-label="Open menu"
              data-testid="app-mobile-toggle"
            >
              <Menu size={16} />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-6 w-6 rounded-md bg-brand-violet text-white text-[11px] font-bold flex items-center justify-center shrink-0">F</div>
              <div className="text-[13.5px] font-semibold text-brand-ink truncate">
                {company?.name || "Fable Ops"}
              </div>
            </div>
          </div>
          <main ref={mainRef} className="flex-1 min-w-0 overflow-y-auto" data-testid="app-main">
            {/* Keyed on pathname so the enter animation retriggers each nav.
                Motion is transform/opacity only (compositor-safe). */}
            <div key={location.pathname} className="animate-page-in">
              <Outlet />
            </div>
          </main>
        </div>
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        <ActiveTimerPill />
        <ConnectionPill connected={connected} />
        <ShortcutsHelp />
      </div>
    </CommsUnreadProvider>
  );
}
